import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setUnit } from '../../Session';
import LibraryCardButton from '../LibraryCardButtons/LibraryCardButton';
import ToggleSlider from '../ToggleSlider';
import styles from './LibraryCard.module.scss';
import { getAppRoot } from '../../identity';
import { getPublicMediaRoot, postServiceRequest } from '../../serviceAgent';
import { createOrUpdatePeekavilleUnitLocks } from '../../core/core';
import { updateClassroom } from '../../Classrooms';
import { updateClassroom as updateClassroomBackend } from '../../BackendInterface';
import { isUserHasKey, getLetterGradeFromNumberGrade, fixedEncodeURIComponent } from '../../GlobalFunctions';
import { RootState } from '../../ApplicationState';
import { LibraryCardProps, UnitCardProps, ElementaryCardData, elementaryUnits } from './cardData';
import { InlineIcon, Icon } from '@iconify/react';
import Tooltip from '../Tooltip';
import eSStudent from '@iconify-icons/openmoji/family';
import lockAlt from '@iconify-icons/bx/lock-alt';
import schoolIcon from '@iconify/icons-icon-park-outline/school';

type Props = LibraryCardProps & UnitCardProps & ElementaryCardData;

const ElementaryLibraryCard = ({
  isShow,
  gradeSpan,
  displayName,
  subtitle,
  content,
  isLocked,
  percentComplete,
  unit,
  classroom,
  hasPeekavilleContent,
  isPeekavilleLocked,
  isWrongGrade,
  hasSpanishContent,
  hasSpanishLibraryCardContent,
  featuredImage,
}: Props): JSX.Element => {
  const [isPeekavilleUnitLocked, setIsPeekavilleUnitLocked] = useState(isPeekavilleLocked);
  const history = useHistory();
  const dispatch = useDispatch();
  const { grade, className } = classroom;
  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });
  const handleClick = () => {
    dispatch(setUnit(unit));
    history.push(`/${fixedEncodeURIComponent(className)}/lessonPlan/${unit}-${getLetterGradeFromNumberGrade(grade)}`);
  };

  const reloadUnitLocks = useCallback(() => {
    setIsPeekavilleUnitLocked(isPeekavilleLocked);
  }, [classroom, isPeekavilleLocked]);

  const togglePeekavilleLock = async (isUnlocked: boolean, unit: string) => {
    setIsPeekavilleUnitLocked(isUnlocked);
    const updatedClassroom = createOrUpdatePeekavilleUnitLocks(isUnlocked, unit, classroom, elementaryUnits);

    const classroomBackupInCaseOfError = { ...classroom };

    const email = userProfile?.email;

    if (!email) {
      console.error('UnitPage::toggleLock - update failed - no email in userProfile');
      dispatch(updateClassroom(classroomBackupInCaseOfError));
      return;
    }

    try {
      const { peekavilleUnitLocks } = updatedClassroom;
      const updatedClassroomResult = await updateClassroomBackend(
        email,
        classroom.className,
        { peekavilleUnitLocks },
        true,
      );
      dispatch(updateClassroom(updatedClassroomResult));
    } catch (error) {
      console.error('UnitPage::toggleLock - postServiceRequest update failed', error);
      dispatch(updateClassroom(classroomBackupInCaseOfError));
    }
  };

  let buttonText = '';
  if (percentComplete === 0) buttonText = 'Explore';
  if (percentComplete === 100) buttonText = 'Complete';
  if (percentComplete !== 100 && percentComplete !== 0) buttonText = 'Resume';

  let unitThumbnailName = unit;
  if (unit === 'Imagination' && parseInt(grade) > 2) {
    unitThumbnailName = 'ImaginationStation';
    featuredImage = `${getPublicMediaRoot()}images/thumbnails/ImaginationStationBookshelf.png`;
  }
  const imgPath =
    classroom && hasSpanishContent && classroom.language !== 'en'
      ? `${getPublicMediaRoot()}images/thumbnails/${classroom.language}${unitThumbnailName}Bookshelf.png`
      : featuredImage;

  const getWrongGradeMessage = () => {
    return unit === 'Math-Anxiety'
      ? `Please switch to a classroom of grade 3, 4 or 5 to access this content`
      : `Please switch to a classroom of grade ${Math.max(...(gradeSpan as number[]))} or lower to access this content`;
  };

  useEffect(() => {
    reloadUnitLocks();
  }, [classroom, isPeekavilleLocked]);
  return isShow ? (
    <div className={styles.container}>
      <div className={styles.imgContainer}>
        {isWrongGrade && (
          <div className={styles.wrongGrade}>
            <Icon icon={eSStudent} width={60} />
            Requires Different Grade Level
          </div>
        )}
        {!isWrongGrade && <img src={imgPath} alt={displayName} onClick={handleClick} />}
        {isLocked && !isWrongGrade && (
          <div className={styles.locked}>
            Limited content...Upgrade for full access
            <span className={styles.upgrade} onClick={() => history.push('/activatetrial')}>
              Upgrade Now
            </span>
          </div>
        )}
        {/*<img src={imgPath} alt={displayName} />*/}
      </div>
      <div className={styles.textContainer}>
        <h3 className={styles.typeHeading}>{subtitle && subtitle}</h3>
        <div className={styles.titleRow}>
          <h2 className={styles.unitTitle}>{displayName && displayName}</h2>
          {!isWrongGrade && !isLocked && hasPeekavilleContent && isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') && (
            <div className={styles.toggleContainer}>
              <ToggleSlider
                isChecked={!isPeekavilleUnitLocked}
                onToggle={() => togglePeekavilleLock(!isPeekavilleUnitLocked, unit.replace('-', ' '))}
                onBackgroundColor='#00ca48'
                offBackgroundColor='#a5a5a5'
                switchColor='#fff'
              />
              <Tooltip
                content={`${isPeekavilleUnitLocked ? 'Unlock' : 'Lock'} this myPeekaville unit`}
                direction='bottom'
                color='blue'
                arrowPosition='right-align'
              >
                <InlineIcon
                  icon={schoolIcon}
                  className={styles.schoolIcon}
                  color={isPeekavilleUnitLocked ? '#a5a5a5' : '#00ca48'}
                />
              </Tooltip>
            </div>
          )}
        </div>
        <p>
          {content}
          {hasSpanishLibraryCardContent && (
            <i className={styles.spanishContentReminder}>
              *Solo el libro y las actividades en casa están disponibles en español
            </i>
          )}
        </p>
        <div className={styles.buttonRow}>
          {isWrongGrade && !isLocked && <p>{getWrongGradeMessage()}</p>}
          {!isWrongGrade && (
            <>
              <LibraryCardButton buttonText={buttonText} handleClick={handleClick} />
              {buttonText === 'Resume' && (
                <div className={styles.completion}>
                  <span className={styles.completionValue}>{percentComplete}</span>% Complete
                </div>
              )}
            </>
          )}
        </div>
        {/* {isLocked && <div className={styles.lockFill} />} */}
      </div>
    </div>
  ) : (
    <></>
  );
};

export default ElementaryLibraryCard;
